import React from 'react';
import { Button, Modal, message } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import Seperator from 'components/UploadPatientListModal/imgs/Seperator.svg';
import Done from 'assets/klarityReview/step.svg';
import ManualInputStep from './components/ManualInputStep';
import ConfirmStep from './components/ConfirmStep';
import { ReviewData } from '../UploadReviewsListModal/types';
import { batchSaveReviews, getUploadedReviewCount } from 'api/klarityReview';

type Props = {
    onCannel?: (needRefresh?: boolean) => void;
    onSuccess?: () => void;
    show?: boolean;
};

const ManualUploadReviewsModal = (props: Props) => {
    const { onCannel, onSuccess, show } = props;
    const [step, setStep] = React.useState(1);
    const [reviews, setReviews] = React.useState<ReviewData[]>([]);
    const [submiting, setSubmiting] = React.useState(false);
    const [uploadedCount, setUploadedCount] = React.useState<number>(0);
    const MAX_UPLOAD_LIMIT = 50; // 最大上传条数限制
    const manualInputStepRef = React.useRef<{ validate:() => boolean; }>(null);

    // 获取已上传条数
    const fetchUploadedCount = React.useCallback(async () => {
        try {
            const result = await getUploadedReviewCount();
            if (result && !result.error && result.data?.code === 200) {
                setUploadedCount(result.data.data || 0);
            } else {
                setUploadedCount(0);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch uploaded count:', e);
            setUploadedCount(0);
        }
    }, []);

    React.useEffect(() => {
        if (!show) {
            setStep(1);
            setReviews([]);
            setSubmiting(false);
            setUploadedCount(0);
        } else {
            // 当 Modal 打开时，获取已上传条数
            fetchUploadedCount();
        }
    }, [show, fetchUploadedCount]);

    const handleNext = async () => {
        if (step === 1) {
            // 如果没有review，不执行任何操作
            if (reviews.length === 0) {
                return;
            }
            // 触发校验（即使只有一个空的review也会触发）
            const isValid = manualInputStepRef.current?.validate() ?? false;
            if (!isValid) {
                // 校验失败，不进入下一步
                return;
            }
            // 验证至少有一个有效的评论（有内容和日期）
            const validReviews = reviews.filter((r) => r.reviewContent && r.reviewContent.trim() && r.date);
            if (validReviews.length === 0) {
                return;
            }
            // 只保留有效的评论
            setReviews(validReviews);
            setStep(step + 1);
            return;
        }
        if (step === 2) {
            if (!reviews || reviews.length === 0) {
                return;
            }

            // 检查上传条数限制
            const remainingQuota = MAX_UPLOAD_LIMIT - uploadedCount;
            if (reviews.length > remainingQuota) {
                message.error(`You can only upload ${remainingQuota} more review(s). The maximum limit is ${MAX_UPLOAD_LIMIT} reviews, and you have already uploaded ${uploadedCount}.`);
                return;
            }

            setSubmiting(true);
            try {
                // 直接使用 reviews，因为字段已经匹配接口格式
                const result = await batchSaveReviews(reviews);

                // 检查返回结果
                if (result && !result.error && result.data?.code === 200) {
                    message.success('Reviews uploaded successfully');
                    // 更新已上传条数
                    await fetchUploadedCount();
                    onSuccess?.();
                } else {
                    const errorMsg = result?.error || result?.data?.message || 'Upload failed';
                    message.error(errorMsg);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to upload reviews:', e);
                message.error('Failed to upload reviews. Please try again.');
            } finally {
                setSubmiting(false);
            }
        }
    };

    // 检查是否有review（用于禁用按钮）
    // 如果没有review，返回false（禁用按钮）
    // 如果有review（即使是空的），返回true（不禁用按钮，但点击时会触发校验）
    const hasReviews = React.useMemo(() => {
        return reviews.length > 0;
    }, [reviews]);

    return (
        <Modal
            title="Add new patient reviews"
            closable
            className={`${commonS.modalFixHeightWrap} ${commonS.modalFixSetedHeightWrap} ${s.modal}`}
            width="1200px"
            onCancel={() => {
                setStep(1);
                setReviews([]);
                onCannel?.();
            }}
            okText={step === 2 ? 'Upload now' : 'Next'}
            open={show}
            footer={
                <div className={s.footer}>
                    {
                        step === 2 ?
                            <Button
                                onClick={() => {
                                    setStep(step - 1);
                                }}
                                loading={submiting}
                            >
                                Back
                            </Button>
                            :
                            <span />
                    }
                    <Button
                        onClick={handleNext}
                        type="primary"
                        loading={submiting}
                        disabled={
                            (step === 1 && !hasReviews) ||
                            (step === 2 && reviews.length === 0)
                        }
                    >
                        {step === 2 ? 'Upload now' : 'Next'}
                    </Button>
                </div>
            }
            destroyOnClose
        >
            <div className={s.wrap}>
                <div className={s.stepWrap}>
                    <div className={step === 1 ? `${s.stepItem} ${s.stepItemActive}` : s.stepItem}>
                        <div className={s.left}>
                            {
                                step > 1 ? <img src={Done} className={s.icon} alt="done" /> : <span className={s.num}>01</span>
                            }
                        </div>
                        <div className={s.right}>
                            <div className={s.title}>
                                ADD PATIENT REVIEWS
                            </div>
                            <div className={s.desc}>
                                Enter review details
                            </div>
                        </div>
                    </div>
                    <div className={step === 2 ? `${s.stepItem} ${s.stepItemActive}` : s.stepItem}>
                        <div className={s.spe}>
                            <img src={Seperator} alt="separator" />
                        </div>
                        <div className={s.left}>
                            <span className={s.num}>02</span>
                        </div>
                        <div className={s.right}>
                            <div className={s.title}>
                                CONFIRM
                            </div>
                            <div className={s.desc}>
                                Confirm patient reviews
                            </div>
                        </div>
                    </div>
                </div>
                {
                    step === 1 &&
                    <ManualInputStep
                        ref={manualInputStepRef}
                        reviews={reviews}
                        onUpdate={(data) => {
                            setReviews(data);
                        }}
                        uploadedCount={uploadedCount}
                        maxUploadLimit={MAX_UPLOAD_LIMIT}
                    />
                }
                {
                    step === 2 &&
                    <ConfirmStep
                        reviews={reviews}
                        onUpdate={(data) => {
                            setReviews(data);
                        }}
                    />
                }
            </div>
        </Modal>
    );
};

export default ManualUploadReviewsModal;
